import { useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtQty, MOVEMENT_LABELS, type LedgerRow } from "@/types/inventory";

const when = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const TYPE_FILTERS = [
  { key: "", label: "All" },
  { key: "GRN_IN,OPENING,GRN_CANCEL", label: "Receipts" },
  { key: "ADJUST_PLUS,ADJUST_MINUS", label: "Adjustments" },
  { key: "TRANSFER_IN,TRANSFER_OUT", label: "Transfers" },
  { key: "DISPATCH_OUT,SALES_RETURN", label: "Sales" },
];

/** Every change to stock, newest first */
export function LedgerTab({ warehouseId, search, reloadKey }: { warehouseId: string; search: string; reloadKey: number }) {
  const { error } = useToast();
  const [type, setType] = useState("");
  const list = usePagedList<LedgerRow>({
    deps: [warehouseId, search, type, reloadKey],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (warehouseId) qs.set("warehouse_id", warehouseId);
      if (search.trim()) qs.set("search", search.trim());
      if (type) qs.set("type", type);
      try {
        return unwrapPage<LedgerRow>(await api.get(`/api/Inventory/Ledger?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load the ledger");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setType(f.key)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${type === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Roll / product</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead>Document / by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs whitespace-nowrap">{when(m.created_on)}</TableCell>
                  <TableCell className="text-sm">
                    {MOVEMENT_LABELS[m.movement_type] || m.movement_type}
                    {m.reason && <div className="text-[11px] text-muted-foreground">{m.reason}</div>}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="font-mono font-semibold">{m.unit_no}</span>
                    <div className="text-[11px] text-muted-foreground">
                      {m.product_name}
                      {m.variant_name ? ` · ${m.variant_name}` : ""}
                      {m.lot_number ? ` · Lot ${m.lot_number}` : ""}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{m.warehouse_name}</TableCell>
                  <TableCell className={`text-right text-sm font-semibold whitespace-nowrap ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.quantity > 0 ? "+" : ""}
                    {fmtQty(m.quantity, m.unit)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <code className="font-mono">{m.ref_number || "-"}</code>
                    {m.created_by_name && <div className="text-muted-foreground">{m.created_by_name}</div>}
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No movements yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
    </div>
  );
}
